import React from 'react';
import { CardSurface, EmptyState, Text, sparkAssetUrl } from '@spark';
import AddDataButton from '@/features/datasets/components/AddDataButton';
import TaskPageTabEmpty from '@/features/tasks/components/TaskPageTabEmpty';
import { formatInFlightRow } from '@/features/datasets/utils/inFlightDataRows';

/** Every Data Editor / Review Training Plan empty state uses the star illustration. */
export const EMPTY_STATE_ILLUSTRATION = 'illustration-no-results';

const COPY = {
    train: {
        title: 'No Data',
        subtitle: 'Upload a dataset, and your training records will appear here.',
    },
    benchmark: {
        title: 'No Benchmark Data',
        subtitle: 'Upload a benchmark dataset, and your evaluation records will appear here.',
    },
    'archived-datasets': {
        title: 'No Archived Datasets',
        subtitle: 'Records you archive from your training set will appear here.',
    },
    'archived-benchmarks': {
        title: 'No Archived Benchmarks',
        subtitle: 'Records you archive from your benchmark will appear here.',
    },
    // Non-blocking Data Collection upload: the PUT is still in flight, so the
    // dataset does not exist on the server yet. No Add Data CTA here — the
    // user already added data; it just hasn't landed.
    uploading: {
        title: 'Uploading your data…',
        subtitle: 'You can keep working - track it in the progress panel.',
    },
    // The file is on the server: the data processor first reads the whole
    // file ("Analyzing file" — nothing is stored yet), then inserts records
    // ("Importing rows"). Rows show up here as they are imported.
    processing: {
        title: 'Processing your data…',
        subtitle: 'Records appear here as they are imported - you can keep working.',
    },
};

const UPLOADABLE_VARIANTS = new Set(['train', 'benchmark']);

/** Format one in-flight row, e.g. `rows.csv · Importing rows · 12%`. */
export const formatUploadRow = formatInFlightRow;

/**
 * Body for the in-flight variant: illustration + title + the list of data
 * flows still running (with their live stage) + the "keep working" line.
 * Also used by Review Training Plan in place of its empty examples table.
 */
export function InFlightDataBody({ title, subtitle, uploads = [], style }) {
    const rows = Array.isArray(uploads) ? uploads : [];
    // Every running flow is past the signed PUT → records can start to land.
    const allProcessing = rows.length > 0 && rows.every((row) => row.processing);
    const copy = allProcessing ? COPY.processing : COPY.uploading;
    return (
        <div
            className="spark-empty-state spark-empty-state-horizontal spark-empty-state-medium fs-unmask"
            style={{ padding: '40px 20px', justifyContent: 'center', ...style }}
            data-testid="data-in-flight-state"
            data-stage={allProcessing ? 'processing' : 'uploading'}
        >
            <div
                className="spark-empty-state-illustration spark-empty-state-illustration-medium"
                aria-hidden="true"
            >
                <img src={sparkAssetUrl(`illustrations/${EMPTY_STATE_ILLUSTRATION}.svg`)} alt="" />
            </div>
            <div className="spark-empty-state-content" style={{ gap: 12 }}>
                <p className="spark-empty-state-title">{title || copy.title}</p>
                {rows.length > 0 && (
                    <ul
                        className="data-table-empty-state__uploads"
                        aria-label="Data flows in progress"
                        style={{ listStyle: 'none', margin: 0, padding: 0, display: 'grid', gap: 4 }}
                    >
                        {rows.map((row, index) => (
                            <li key={`${row.name}-${index}`}>
                                <Text variant="base-medium" color="primary">
                                    {formatInFlightRow(row)}
                                </Text>
                            </li>
                        ))}
                    </ul>
                )}
                <p className="spark-empty-state-subtitle">{subtitle || copy.subtitle}</p>
            </div>
        </div>
    );
}

const INLINE_WRAPPER_STYLE = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '100%',
    minHeight: '40vh',
    padding: '24px 0',
};

/**
 * Empty-state body (illustration + copy + optional Add Data CTA). Shared by
 * the page-level and in-tab layouts below.
 */
function EmptyStateBody({ title, subtitle, showAddData, usecase, uploadTarget }) {
    if (showAddData) {
        return (
            <div
                className="spark-empty-state spark-empty-state-horizontal spark-empty-state-medium fs-unmask"
                style={{ padding: '40px 20px', justifyContent: 'center' }}
            >
                <div
                    className="spark-empty-state-illustration spark-empty-state-illustration-medium"
                    aria-hidden="true"
                >
                    <img
                        src={sparkAssetUrl(`illustrations/${EMPTY_STATE_ILLUSTRATION}.svg`)}
                        alt=""
                    />
                </div>
                <div className="spark-empty-state-content" style={{ gap: 12 }}>
                    <p className="spark-empty-state-title">{title}</p>
                    <p className="spark-empty-state-subtitle">{subtitle}</p>
                    <div style={{ marginTop: 4 }}>
                        <AddDataButton
                            usecase={usecase}
                            placement="bottom"
                            uploadTarget={uploadTarget}
                        />
                    </div>
                </div>
            </div>
        );
    }

    return (
        <EmptyState
            size="medium"
            title={title}
            subtitle={subtitle}
            illustration={EMPTY_STATE_ILLUSTRATION}
        />
    );
}

/**
 * Empty state shown in the Data Editor when a tab (or the whole editor) has
 * no records.
 *
 * - `layout="page"` — full-width TaskPageTabEmpty chrome (no side tabs), same
 *   as Evaluations / Model Details empty states.
 * - `layout="inline"` (default) — bordered card inside the active side-tab pane.
 * - `variant="uploading"` — data flows still running (`uploads` rows); the
 *   title reads "Processing your data…" once every row is past upload.
 */
const DataTableEmptyState = ({
    variant = 'train',
    title,
    subtitle,
    usecase = null,
    uploadTarget,
    layout = 'inline',
    /** `uploading` variant only: rows from `inFlightRowFromEntry`. */
    uploads = [],
}) => {
    const copy = COPY[variant] || COPY.train;
    const showAddData = Boolean(usecase) && UPLOADABLE_VARIANTS.has(variant);
    const isUploading = variant === 'uploading';

    const body = isUploading ? (
        <InFlightDataBody title={title} subtitle={subtitle} uploads={uploads} />
    ) : (
        <EmptyStateBody
            title={title || copy.title}
            subtitle={subtitle || copy.subtitle}
            showAddData={showAddData}
            usecase={usecase}
            uploadTarget={uploadTarget}
        />
    );

    if (layout === 'page') {
        return <TaskPageTabEmpty>{body}</TaskPageTabEmpty>;
    }

    return (
        <div className="data-table-empty-state" style={INLINE_WRAPPER_STYLE}>
            <CardSurface bordered padding="large" style={{ width: '100%' }}>
                {showAddData || isUploading ? (
                    body
                ) : (
                    <div
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            width: '100%',
                            padding: '40px 24px',
                        }}
                    >
                        {body}
                    </div>
                )}
            </CardSurface>
        </div>
    );
};

export default DataTableEmptyState;
